import { z } from 'zod'
import type { UserSettings, UserSettingsNotification, UserSettingsPos, UserSettingsSupport } from '~/domain/dashboard/user/users/models/UserSettings'

const UserSettingsNotificationDTO = z.object({
  receive_financial_emails: z.boolean(),
  receive_contract_emails: z.boolean()
})

const UserSettingsPosDTO = z.object({
  show_in_list_on_open: z.boolean(),
  show_in_sellers_list: z.boolean(),
  table_service_password: z.string().nullish()
})

const UserSettingsSupportDTO = z.object({
  access_pin: z.number().nullish()
})

type UserSettingsNotificationDTO = z.infer<typeof UserSettingsNotificationDTO>
type UserSettingsPosDTO = z.infer<typeof UserSettingsPosDTO>
type UserSettingsSupportDTO = z.infer<typeof UserSettingsSupportDTO>

export const UserSettingsDTO = z.object({
  notifications: UserSettingsNotificationDTO,
  pos: UserSettingsPosDTO,
  support: UserSettingsSupportDTO.nullish()
})

export type UserSettingsDTO = z.infer<typeof UserSettingsDTO>

function mapUserSettingsNotificationToDTO (notification: UserSettingsNotification): UserSettingsNotificationDTO {
  return {
    receive_financial_emails: notification.receiveFinancialEmails,
    receive_contract_emails: notification.receiveContractEmails
  }
}

function mapUserSettingsPosToDTO (pos: UserSettingsPos): UserSettingsPosDTO {
  return {
    show_in_list_on_open: pos.showInListOnOpen,
    show_in_sellers_list: pos.showInSellersList,
    table_service_password: pos.tableServicePassword
  }
}

function mapUserSettingsSupportToDTO (support: UserSettingsSupport): UserSettingsSupportDTO {
  return {
    access_pin: support.accessPin
  }
}

function mapDTOToUserSettingsNotification (dto: UserSettingsNotificationDTO): UserSettingsNotification {
  return {
    receiveFinancialEmails: dto.receive_financial_emails,
    receiveContractEmails: dto.receive_contract_emails
  }
}

function mapDTOToUserSettingsPos (dto: UserSettingsPosDTO): UserSettingsPos {
  return {
    showInListOnOpen: dto.show_in_list_on_open,
    showInSellersList: dto.show_in_sellers_list,
    tableServicePassword: dto.table_service_password
  }
}

function mapDTOToUserSettingsSupport (dto: UserSettingsSupportDTO): UserSettingsSupport {
  return {
    accessPin: dto.access_pin ?? undefined
  }
}

export function mapUserSettingsToDTO (user: UserSettings): UserSettingsDTO {
  return {
    notifications: mapUserSettingsNotificationToDTO(user.notifications),
    pos: mapUserSettingsPosToDTO(user.pos),
    support: user.support ? mapUserSettingsSupportToDTO(user.support) : undefined
  }
}

export function mapDTOToUserSettings (dto: UserSettingsDTO): UserSettings {
  return {
    notifications: mapDTOToUserSettingsNotification(dto.notifications),
    pos: mapDTOToUserSettingsPos(dto.pos),
    support: dto.support ? mapDTOToUserSettingsSupport(dto.support) : undefined
  }
}
